import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "./NavBar";
import { uploadPhoto } from "./uploadPhoto";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

function timeLeft(endsAt) {
  const diff = new Date(endsAt) - new Date();
  if (diff <= 0) return "Ended";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (d > 0) return `${d}d ${h}h left`;
  if (h > 0) return `${h}h ${m}m left`;
  if (m > 0) return `${m}m ${s}s left`;
  return `${s}s left`;
}

function AuctionCard({ auction, user, onBid, onBuyNow, onClick }) {
  const [timeStr, setTimeStr] = useState(timeLeft(auction.endsAt));
  const isEnded = auction.status === "ENDED" || auction.status === "CANCELED" || new Date(auction.endsAt) <= new Date();
  const currentPrice = auction.currentBid ? Number(auction.currentBid) : Number(auction.startingPrice);
  const isWinning = auction.winnerId === user?.id || auction.bids?.find(b => b.bidderId === user?.id && b.isWinning);
  const myBid = auction.bids?.find(b => b.bidderId === user?.id);
  const isSeller = auction.sellerId === user?.id;
  const totalBids = auction.bids?.length || 0;

  useEffect(() => {
    if (isEnded) return;
    const t = setInterval(() => setTimeStr(timeLeft(auction.endsAt)), 1000);
    return () => clearInterval(t);
  }, [auction.endsAt, isEnded]);

  const s = {
    card: { background: "rgba(255,255,255,0.75)", border: `1px solid ${isWinning && !isEnded ? "rgba(22,163,74,0.3)" : "rgba(59,130,246,0.12)"}`, borderRadius: 16, overflow: "hidden", cursor: "pointer", backdropFilter: "blur(8px)", boxShadow: `0 2px 12px ${isWinning && !isEnded ? "rgba(22,163,74,0.1)" : "rgba(59,130,246,0.08)"}`, transition: "transform 0.2s, box-shadow 0.2s" },
    img: { width: "100%", height: 180, objectFit: "cover" },
    placeholder: { width: "100%", height: 180, background: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(124,58,237,0.1))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 48 },
    body: { padding: "14px 16px" },
    timer: { fontSize: 11, fontWeight: 700, color: isEnded ? "#6b7280" : new Date(auction.endsAt) - new Date() < 3600000 ? "#dc2626" : "#d97706", marginBottom: 6 },
    title: { fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    price: { fontSize: 22, fontWeight: 800, color: "#1e40af", marginBottom: 2 },
    bids: { fontSize: 11, color: "#94a3b8", marginBottom: 10 },
    winBadge: { display: "inline-block", background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.25)", borderRadius: 999, padding: "2px 8px", fontSize: 10, fontWeight: 700, color: "#16a34a", marginBottom: 8 },
    outbidBadge: { display: "inline-block", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 999, padding: "2px 8px", fontSize: 10, fontWeight: 700, color: "#dc2626", marginBottom: 8 },
    bidBtn: { width: "100%", background: "#3b82f6", color: "#fff", border: "none", padding: "10px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 13, marginBottom: 6 },
    buyNowBtn: { width: "100%", background: "rgba(22,163,74,0.1)", color: "#16a34a", border: "1px solid rgba(22,163,74,0.25)", padding: "9px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 12 },
    endedBadge: { display: "inline-block", background: "rgba(107,114,128,0.1)", borderRadius: 999, padding: "2px 8px", fontSize: 10, fontWeight: 700, color: "#6b7280" },
  };

  return (
    <div
      style={s.card}
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(59,130,246,0.15)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(59,130,246,0.08)"; }}
    >
      {auction.photoUrls?.[0] ? (
        <img src={auction.photoUrls[0]} alt={auction.title} style={s.img} />
      ) : (
        <div style={s.placeholder}>🏷️</div>
      )}
      <div style={s.body}>
        <div style={s.timer}>{isEnded ? "⏹ Ended" : `⏱ ${timeStr}`}</div>
        <div style={s.title}>{auction.title}</div>
        <div style={s.price}>${currentPrice.toFixed(2)}</div>
        <div style={s.bids}>{totalBids} bid{totalBids !== 1 ? "s" : ""} · Starting $${Number(auction.startingPrice).toFixed(2)}</div>
        {auction.city && <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>📍 {auction.city}{auction.state ? `, ${auction.state}` : ""}</div>}

        {!isEnded && !isSeller && (
          <>
            {isWinning && <div style={s.winBadge}>✅ You're winning</div>}
            {myBid && !isWinning && <div style={s.outbidBadge}>⚡ You've been outbid</div>}
            <button style={s.bidBtn} onClick={e => { e.stopPropagation(); onBid(auction); }}>
              Place Bid
            </button>
            {auction.buyNowPrice && (
              <button style={s.buyNowBtn} onClick={e => { e.stopPropagation(); onBuyNow(auction); }}>
                Buy Now · ${Number(auction.buyNowPrice).toFixed(2)}
              </button>
            )}
          </>
        )}
        {isEnded && (
          <div style={s.endedBadge}>
            {auction.winnerId === user?.id ? "🎉 You won!" : auction.winnerId ? "Sold" : "No winner"}
          </div>
        )}
        {isSeller && !isEnded && <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Your auction</div>}
      </div>
    </div>
  );
}

export default function AuctionsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [subTab, setSubTab] = useState("browse"); // browse | mybids | myselling
  const [auctions, setAuctions] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [mySelling, setMySelling] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [bidAmount, setBidAmount] = useState("");
  const [bidding, setBidding] = useState(false);
  const [bidError, setBidError] = useState("");
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    title: "", description: "", startingPrice: "", reservePrice: "",
    buyNowPrice: "", endsAt: "", city: "", state: "", isInPerson: false
  });

  const loadData = useCallback(async () => {
    try {
      const [ar, br, sr] = await Promise.all([
        fetch(`${API_BASE_URL}/auctions`, { headers: authHeaders() }),
        fetch(`${API_BASE_URL}/auctions/my/bids`, { headers: authHeaders() }),
        fetch(`${API_BASE_URL}/auctions/my/selling`, { headers: authHeaders() }),
      ]);
      const [ad, bd, sd] = await Promise.all([ar.json(), br.json(), sr.json()]);
      if (ar.ok) setAuctions(Array.isArray(ad) ? ad : []);
      if (br.ok) setMyBids(Array.isArray(bd) ? bd : []);
      if (sr.ok) setMySelling(Array.isArray(sd) ? sd : []);
    } catch (err) { console.warn(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  async function handlePhotoUpload(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const { uploadPhoto } = await import("./uploadPhoto");
      const urls = await Promise.all(files.map(f => uploadPhoto(f)));
      setPhotos(prev => [...prev, ...urls]);
    } catch { window.alert("Photo upload failed."); }
    finally { setUploading(false); e.target.value = ""; }
  }

  async function handleCreate() {
    if (!form.title.trim()) { window.alert("Title is required"); return; }
    if (!form.startingPrice || Number(form.startingPrice) <= 0) { window.alert("Starting price is required"); return; }
    if (!form.endsAt) { window.alert("End time is required"); return; }
    if (new Date(form.endsAt) <= new Date()) { window.alert("End time must be in the future"); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auctions`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ ...form, photoUrls: photos, startingPrice: Number(form.startingPrice), reservePrice: form.reservePrice ? Number(form.reservePrice) : null, buyNowPrice: form.buyNowPrice ? Number(form.buyNowPrice) : null })
      });
      const data = await res.json();
      if (res.ok) {
        setShowCreate(false);
        setForm({ title: "", description: "", startingPrice: "", reservePrice: "", buyNowPrice: "", endsAt: "", city: "", state: "", isInPerson: false });
        setPhotos([]);
        loadData();
        setSubTab("myselling");
      } else { window.alert(data.error || "Could not create auction"); }
    } catch { window.alert("Something went wrong"); }
    finally { setCreating(false); }
  }

  async function handleBid() {
    if (!bidAmount || Number(bidAmount) <= 0) { setBidError("Enter a valid bid amount"); return; }
    const minBid = selectedAuction.currentBid
      ? Number(selectedAuction.currentBid) + 0.01
      : Number(selectedAuction.startingPrice);
    if (Number(bidAmount) < minBid) { setBidError(`Minimum bid is $${minBid.toFixed(2)}`); return; }
    setBidding(true); setBidError("");
    try {
      const res = await fetch(`${API_BASE_URL}/auctions/${selectedAuction.id}/bid`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ amount: Number(bidAmount) })
      });
      const data = await res.json();
      if (res.ok) { setShowBidModal(false); setBidAmount(""); loadData(); }
      else { setBidError(data.error || "Could not place bid"); }
    } catch { setBidError("Something went wrong"); }
    finally { setBidding(false); }
  }

  async function handleBuyNow(auction) {
    if (!window.confirm(`Buy "${auction.title}" now for $${Number(auction.buyNowPrice).toFixed(2)}?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/auctions/${auction.id}/buy-now`, {
        method: "POST", headers: authHeaders()
      });
      const data = await res.json();
      if (res.ok) { window.alert("Purchase confirmed! Contact the seller to arrange delivery."); loadData(); }
      else { window.alert(data.error || "Could not complete purchase"); }
    } catch { window.alert("Something went wrong"); }
  }

  async function handleCancel(auctionId) {
    if (!window.confirm("Cancel this auction? All bidders will be notified.")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/auctions/${auctionId}/cancel`, {
        method: "PUT", headers: authHeaders()
      });
      if (res.ok) loadData();
      else { const d = await res.json(); window.alert(d.error || "Could not cancel"); }
    } catch { window.alert("Something went wrong"); }
  }

  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 1100, margin: "0 auto", padding: "24px 20px" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
    title: { fontSize: 26, fontWeight: 800, color: "#0f172a" },
    createBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: "11px 20px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, boxShadow: "0 4px 12px rgba(59,130,246,0.25)" },
    tabBar: { display: "flex", gap: 8, marginBottom: 24 },
    tabBtn: (active) => ({ padding: "8px 18px", borderRadius: 10, border: "1px solid", borderColor: active ? "#3b82f6" : "rgba(59,130,246,0.15)", background: active ? "#3b82f6" : "rgba(255,255,255,0.7)", color: active ? "#fff" : "#475569", cursor: "pointer", fontWeight: 700, fontSize: 13 }),
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16 },
    empty: { textAlign: "center", padding: "60px 20px", color: "#94a3b8" },
    overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)", overflowY: "auto" },
    modal: { background: "#fff", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 20, padding: 28, width: "100%", maxWidth: 500, boxShadow: "0 20px 60px rgba(59,130,246,0.15)", maxHeight: "90vh", overflowY: "auto" },
    modalTitle: { fontSize: 20, fontWeight: 800, marginBottom: 6, color: "#0f172a" },
    modalSub: { fontSize: 13, color: "#64748b", marginBottom: 20 },
    label: { fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 5, display: "block" },
    input: { width: "100%", padding: "11px 13px", background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 12, boxSizing: "border-box", outline: "none" },
    textarea: { width: "100%", padding: "11px 13px", background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 12, boxSizing: "border-box", outline: "none", minHeight: 70, resize: "vertical" },
    twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
    error: { background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#dc2626", marginBottom: 12 },
    actions: { display: "flex", gap: 10, marginTop: 8 },
    cancelBtn: { flex: 1, background: "transparent", border: "1px solid rgba(59,130,246,0.2)", color: "#64748b", padding: "12px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14 },
    submitBtn: { flex: 2, background: "#3b82f6", color: "#fff", border: "none", padding: "12px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14 },
    uploadBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px", background: "rgba(59,130,246,0.06)", border: "1px dashed rgba(59,130,246,0.3)", borderRadius: 10, color: "#3b82f6", cursor: "pointer", fontWeight: 700, fontSize: 13, marginBottom: 12, width: "100%", boxSizing: "border-box" },
    photoGrid: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 },
    // My bids card
    bidCard: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 14, padding: "14px 16px", marginBottom: 10, display: "flex", gap: 12, alignItems: "center", backdropFilter: "blur(8px)", boxShadow: "0 2px 8px rgba(59,130,246,0.06)", cursor: "pointer" },
  };

  if (loading) return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", color: "#94a3b8" }}>Loading...</div>
    </div>
  );

  // Min datetime for auction end (at least 1 hour from now)
  const minDateTime = new Date(Date.now() + 3600000).toISOString().slice(0, 16);

  return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={s.main}>
        <div style={s.header}>
          <div>
            <div style={s.title}>🏷️ Auctions</div>
            <div style={{ fontSize: 14, color: "#64748b", marginTop: 4 }}>Bid on items or create your own auction</div>
          </div>
          <button style={s.createBtn} onClick={() => setShowCreate(true)}>+ Create Auction</button>
        </div>

        <div style={s.tabBar}>
          <button style={s.tabBtn(subTab === "browse")} onClick={() => setSubTab("browse")}>Browse Auctions</button>
          <button style={s.tabBtn(subTab === "mybids")} onClick={() => setSubTab("mybids")}>
            My Bids {myBids.length > 0 ? `(${myBids.length})` : ""}
          </button>
          <button style={s.tabBtn(subTab === "myselling")} onClick={() => setSubTab("myselling")}>
            My Auctions {mySelling.length > 0 ? `(${mySelling.length})` : ""}
          </button>
        </div>

        {/* Browse */}
        {subTab === "browse" && (
          auctions.length === 0 ? (
            <div style={s.empty}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🏷️</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: "#475569" }}>No active auctions</div>
              <div>Be the first to create one!</div>
            </div>
          ) : (
            <div style={s.grid}>
              {auctions.map(auction => (
                <AuctionCard
                  key={auction.id}
                  auction={auction}
                  user={user}
                  onBid={(a) => { setSelectedAuction(a); setBidAmount(""); setBidError(""); setShowBidModal(true); }}
                  onBuyNow={handleBuyNow}
                  onClick={() => navigate(`/auctions/${auction.id}`)}
                />
              ))}
            </div>
          )
        )}

        {/* My Bids */}
        {subTab === "mybids" && (
          myBids.length === 0 ? (
            <div style={s.empty}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🎯</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: "#475569" }}>No bids yet</div>
              <div>Browse auctions and place your first bid</div>
            </div>
          ) : myBids.map(bid => {
            const auction = bid.auction;
            const isWinning = bid.isWinning;
            const isEnded = auction.status === "ENDED" || auction.status === "CANCELED";
            const won = isEnded && auction.winnerId === user?.id;
            return (
              <div key={bid.id} style={s.bidCard} onClick={() => navigate(`/auctions/${auction.id}`)}>
                {auction.photoUrls?.[0] ? (
                  <img src={auction.photoUrls[0]} alt={auction.title} style={{ width: 52, height: 52, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 52, height: 52, borderRadius: 10, background: "rgba(59,130,246,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🏷️</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{auction.title}</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>Your bid: <strong style={{ color: "#1e40af" }}>${Number(bid.amount).toFixed(2)}</strong></div>
                </div>
                <div style={{ flexShrink: 0, textAlign: "right" }}>
                  {won && <div style={{ fontSize: 11, fontWeight: 700, color: "#16a34a" }}>🎉 Won</div>}
                  {!isEnded && isWinning && <div style={{ fontSize: 11, fontWeight: 700, color: "#16a34a" }}>✅ Winning</div>}
                  {!isEnded && !isWinning && <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626" }}>⚡ Outbid</div>}
                  {isEnded && !won && <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280" }}>Ended</div>}
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{isEnded ? "Closed" : timeLeft(auction.endsAt)}</div>
                </div>
              </div>
            );
          })
        )}

        {/* My Selling */}
        {subTab === "myselling" && (
          mySelling.length === 0 ? (
            <div style={s.empty}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: "#475569" }}>No auctions yet</div>
              <div>Create your first auction to start selling</div>
            </div>
          ) : mySelling.map(auction => {
            const isEnded = auction.status === "ENDED" || auction.status === "CANCELED";
            const currentPrice = auction.currentBid ? Number(auction.currentBid) : Number(auction.startingPrice);
            return (
              <div key={auction.id} style={{ ...s.bidCard, cursor: "default" }}>
                {auction.photoUrls?.[0] ? (
                  <img src={auction.photoUrls[0]} alt={auction.title} style={{ width: 52, height: 52, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 52, height: 52, borderRadius: 10, background: "rgba(59,130,246,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🏷️</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{auction.title}</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    Current: <strong style={{ color: "#1e40af" }}>${currentPrice.toFixed(2)}</strong> · {auction.bids?.length || 0} bids
                  </div>
                  {!isEnded && <div style={{ fontSize: 11, color: "#d97706", marginTop: 2 }}>⏱ {timeLeft(auction.endsAt)}</div>}
                </div>
                <div style={{ flexShrink: 0, textAlign: "right", display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: isEnded ? "#6b7280" : "#16a34a" }}>
                    {auction.status === "CANCELED" ? "Canceled" : isEnded ? "Ended" : "Active"}
                  </div>
                  {!isEnded && (
                    <button
                      style={{ background: "rgba(220,38,38,0.08)", color: "#dc2626", border: "1px solid rgba(220,38,38,0.2)", padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700 }}
                      onClick={() => handleCancel(auction.id)}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Bid Modal */}
      {showBidModal && selectedAuction && (
        <div style={s.overlay} onClick={() => setShowBidModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Place a Bid</div>
            <div style={s.modalSub}>{selectedAuction.title}</div>
            <div style={{ background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 10, padding: "12px 14px", marginBottom: 16, fontSize: 13 }}>
              <div style={{ color: "#64748b", marginBottom: 4 }}>Current highest bid</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#1e40af" }}>
                ${selectedAuction.currentBid ? Number(selectedAuction.currentBid).toFixed(2) : Number(selectedAuction.startingPrice).toFixed(2)}
              </div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                Minimum bid: ${selectedAuction.currentBid
                  ? (Number(selectedAuction.currentBid) + 0.01).toFixed(2)
                  : Number(selectedAuction.startingPrice).toFixed(2)}
              </div>
            </div>
            <label style={s.label}>Your Bid ($)</label>
            <input
              style={s.input}
              type="number"
              step="0.01"
              min={selectedAuction.currentBid ? Number(selectedAuction.currentBid) + 0.01 : Number(selectedAuction.startingPrice)}
              placeholder="Enter bid amount"
              value={bidAmount}
              onChange={e => setBidAmount(e.target.value)}
              autoFocus
              onKeyDown={e => e.key === "Enter" && handleBid()}
            />
            {bidError && <div style={s.error}>{bidError}</div>}
            {selectedAuction.buyNowPrice && (
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
                💡 Buy Now available at ${Number(selectedAuction.buyNowPrice).toFixed(2)}
              </div>
            )}
            <div style={s.actions}>
              <button style={s.cancelBtn} onClick={() => setShowBidModal(false)}>Cancel</button>
              <button
                style={{ ...s.submitBtn, opacity: bidding ? 0.5 : 1 }}
                onClick={handleBid}
                disabled={bidding}
              >
                {bidding ? "Placing bid..." : "Place Bid"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Auction Modal */}
      {showCreate && (
        <div style={s.overlay} onClick={() => setShowCreate(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Create Auction</div>
            <div style={s.modalSub}>Set up your auction listing</div>

            <label style={s.label}>Title *</label>
            <input style={s.input} placeholder="e.g. Vintage Camera" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />

            <label style={s.label}>Description</label>
            <textarea style={s.textarea} placeholder="Describe your item..." value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />

            <div style={s.twoCol}>
              <div>
                <label style={s.label}>Starting Price ($) *</label>
                <input style={s.input} type="number" step="0.01" min="0.01" placeholder="1.00" value={form.startingPrice} onChange={e => setForm(f => ({ ...f, startingPrice: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Reserve Price ($)</label>
                <input style={s.input} type="number" step="0.01" placeholder="Optional" value={form.reservePrice} onChange={e => setForm(f => ({ ...f, reservePrice: e.target.value }))} />
              </div>
            </div>

            <label style={s.label}>Buy Now Price ($) <span style={{ color: "#94a3b8", fontWeight: 400 }}>— optional, ends auction instantly</span></label>
            <input style={s.input} type="number" step="0.01" placeholder="Optional" value={form.buyNowPrice} onChange={e => setForm(f => ({ ...f, buyNowPrice: e.target.value }))} />

            <label style={s.label}>Auction End Time *</label>
            <input style={s.input} type="datetime-local" min={minDateTime} value={form.endsAt} onChange={e => setForm(f => ({ ...f, endsAt: e.target.value }))} />

            <div style={s.twoCol}>
              <div>
                <label style={s.label}>City</label>
                <input style={s.input} placeholder="e.g. Miami" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>State</label>
                <input style={s.input} placeholder="FL" maxLength={2} value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value.toUpperCase() }))} />
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <input
                type="checkbox"
                id="inPersonAuction"
                checked={form.isInPerson}
                onChange={e => setForm(f => ({ ...f, isInPerson: e.target.checked }))}
                style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#3b82f6" }}
              />
              <label htmlFor="inPersonAuction" style={{ fontSize: 13, color: "#475569", cursor: "pointer" }}>
                🤝 Local pickup / in-person only
              </label>
            </div>

            <label style={s.label}>Photos</label>
            {photos.length > 0 && (
              <div style={s.photoGrid}>
                {photos.map((url, i) => (
                  <div key={i} style={{ position: "relative" }}>
                    <img src={url} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: "1px solid rgba(59,130,246,0.15)" }} />
                    <button
                      onClick={() => setPhotos(prev => prev.filter((_, pi) => pi !== i))}
                      style={{ position: "absolute", top: -5, right: -5, background: "#ef4444", border: "none", borderRadius: "50%", width: 18, height: 18, color: "#fff", cursor: "pointer", fontSize: 9, fontWeight: 700 }}
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
            {uploading ? (
              <div style={{ ...s.uploadBtn, opacity: 0.6 }}>⏳ Uploading...</div>
            ) : (
              <>
                <div style={s.uploadBtn} onClick={() => fileRef.current?.click()}>📷 Add Photos</div>
                <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handlePhotoUpload} />
              </>
            )}

            <div style={s.actions}>
              <button style={s.cancelBtn} onClick={() => setShowCreate(false)}>Cancel</button>
              <button style={{ ...s.submitBtn, opacity: creating ? 0.5 : 1 }} onClick={handleCreate} disabled={creating}>
                {creating ? "Creating..." : "Create Auction"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}